import { useI18n } from '@/shared/i18n';
import { Button } from '@/presentation/components/atoms/Button';
import { Input } from '@/presentation/components/atoms/Input';
import { Switch } from '@/presentation/components/atoms/Switch';
import { SearchableSelect } from '@/core/ui/components';
import { TIMEZONE_SELECT_OPTIONS } from '@/shared/constants/timezones';
import type { Routine, RoutineActionCatalogEntry, RoutineCategory } from '@yawi/types';
import { Check, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useRoutineForm } from '../hooks/useRoutineForm';
import { CronBuilder } from './CronBuilder';

interface Props {
  routine?: Routine | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  billing: 'Facturación',
  access: 'Control de Acceso',
  gdpr: 'GDPR / Privacidad',
  restrictions: 'Restricciones',
  maintenance: 'Mantenimiento',
  custom: 'Personalizado',
};

const CATEGORY_LABELS_FULL: Record<string, string> = { ...CATEGORY_LABELS };

function groupActionsByCategory(actions: RoutineActionCatalogEntry[]) {
  const groups: Record<string, RoutineActionCatalogEntry[]> = {};
  for (const action of actions) {
    const cat = action.category ?? 'custom';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(action);
  }
  return groups;
}

function SectionDivider() {
  return <div className="border-t border-slate-100" />;
}

export const RoutineFormModal: React.FC<Props> = ({ routine, onClose, onSuccess }) => {
  const { t } = useI18n();
  const { form, setField, actions, loadingActions, cronResult, validatingCron, validateCron, saving, submit, submitError } =
    useRoutineForm(routine, () => { onSuccess(); onClose(); });

  const [selectedActionCode, setSelectedActionCode] = useState(routine?.action ?? '');

  const isEdit = !!routine;
  const isSystem = routine?.isSystem ?? false;
  const title = isEdit ? t('routines.modal.editTitle') : t('routines.modal.createTitle');

  const normalizedActions = actions.map((a, idx) => ({
    ...a,
    actionCode: a.actionCode?.trim() ? a.actionCode : `action-${idx}`,
  }));
  const grouped = groupActionsByCategory(normalizedActions);

  const categoryOptions = useMemo(() => {
    const withActions = Object.keys(grouped).filter((cat) => (grouped[cat] ?? []).length > 0);
    return withActions.map((cat) => ({
      value: cat,
      label: CATEGORY_LABELS_FULL[cat] ?? cat,
    }));
  }, [grouped]);

  const handleActionSelect = (code: string) => {
    if (!code) return;
    setSelectedActionCode(code);
    setField('action', code);
    const found = normalizedActions.find((a) => a.actionCode === code);
    if (found) setField('category', found.category as RoutineCategory);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await submit();
    } catch {
      // El error ya se muestra vía submitError; no propagar al submit del form.
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-6 space-y-5">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('routines.form.name')}</label>
            <Input
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Ej. Cargos de mensualidad"
              disabled={isSystem}
              required={!isSystem}
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('routines.form.description')}</label>
            <Input
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="Descripción opcional de qué hace esta rutina"
            />
          </div>

          {/* Acción + Categoría — sólo editable para rutinas no-sistema */}
          {!isSystem && (
            <>
              <SectionDivider />

              {/* Categoría — controla qué acciones se muestran */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('routines.form.category')}</label>
                <SearchableSelect
                  options={categoryOptions}
                  value={form.category && categoryOptions.some((o) => o.value === form.category) ? form.category : ''}
                  onChange={(val) => {
                    const category = (val ?? '') as RoutineCategory;
                    setField('category', category);
                    if (selectedActionCode) {
                      const found = normalizedActions.find((a) => a.actionCode === selectedActionCode);
                      if (found && found.category !== category) {
                        setSelectedActionCode('');
                        setField('action', '');
                      }
                    }
                  }}
                  placeholder="Selecciona una categoría..."
                  isSearchable
                />
                <p className="mt-1.5 text-xs text-slate-400">
                  Elige una categoría para ver sus automatizaciones disponibles.
                </p>
              </div>

              {/* Acción — tarjetas según la categoría elegida */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('routines.form.action')}</label>
                {loadingActions ? (
                  <p className="text-sm text-slate-400">Cargando acciones...</p>
                ) : categoryOptions.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 px-4 py-5 text-center">
                    <p className="text-sm text-slate-400">No hay automatizaciones disponibles.</p>
                  </div>
                ) : form.category && categoryOptions.some((o) => o.value === form.category) ? (
                  (() => {
                    const items = grouped[form.category] ?? [];
                    if (items.length === 0) {
                      return <p className="text-sm text-slate-400">Sin acciones para esta categoría.</p>;
                    }
                    return (
                      <div className="grid gap-2">
                        {items.map((action) => {
                          const isSelected = selectedActionCode === action.actionCode;
                          return (
                            <button
                              key={action.actionCode}
                              type="button"
                              aria-pressed={isSelected}
                              onClick={() => handleActionSelect(action.actionCode)}
                              className={`group text-left px-3.5 py-2.5 rounded-lg border text-sm transition-colors flex items-start gap-3 ${
                                isSelected
                                  ? 'border-sky-400 ring-1 ring-sky-200 bg-sky-50'
                                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              <span
                                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                                  isSelected
                                    ? 'border-sky-500 bg-sky-500 text-white'
                                    : 'border-slate-300 text-transparent group-hover:border-slate-400'
                                }`}
                              >
                                <Check size={12} strokeWidth={3} />
                              </span>
                              <span className="flex-1">
                                <span
                                  className={`font-medium block ${isSelected ? 'text-sky-800' : 'text-slate-700'}`}
                                >
                                  {action.displayName}
                                </span>
                                <span className="text-xs text-slate-500 block mt-0.5">{action.description}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-200 px-4 py-5 text-center">
                    <p className="text-sm text-slate-400">Selecciona una categoría arriba para ver sus acciones.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Frecuencia — CronBuilder */}
          <SectionDivider />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('routines.triggerSummary')}</label>
            <CronBuilder
              value={form.cronExpression}
              onChange={(cron) => setField('cronExpression', cron)}
              cronValidation={cronResult}
              onValidate={validateCron}
              validating={validatingCron}
            />
          </div>

          {/* Zona horaria */}
          <SectionDivider />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('routines.form.timezone')}</label>
            <SearchableSelect
              options={TIMEZONE_SELECT_OPTIONS}
              value={form.timezone}
              onChange={(val) => setField('timezone', val ?? 'UTC')}
              placeholder="Busca una zona horaria..."
              isSearchable
            />
          </div>

          <SectionDivider />

          {/* Activar al crear/guardar */}
          <Switch
            checked={form.enabled}
            onChange={(checked) => setField('enabled', checked)}
            label={t('routines.form.enabled')}
          />
        </form>

        <div className="flex flex-col gap-2 px-6 py-4 border-t border-slate-200">
          {submitError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {submitError}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('routines.form.cancel')}
            </Button>
            <Button type="submit" onClick={handleSubmit} disabled={saving || (!isSystem && !selectedActionCode)}>
              {saving ? 'Guardando...' : t('routines.form.save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
